import styled, { keyframes } from 'styled-components';

const overlayFadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const cardPopIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(12px) scale(0.97);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const cardSlideUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const ConversationModalOverlay = styled.div`
  position: fixed;
  z-index: 99999;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(20, 30, 25, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  animation: ${overlayFadeIn} 0.18s ease-out;

  @media only screen and (max-width: 576px) {
    padding: 0;
    display: block;
  }
`;

export const ConversationModalCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 550px;
  height: 80vh;
  max-height: 640px;
  background: #ffffff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.25);
  animation: ${cardPopIn} 0.22s cubic-bezier(0.16, 1, 0.3, 1);
  transition: transform 0.15s ease-out;
  transform: ${(props) => (props.$nudge ? 'scale(1.02)' : 'scale(1)')};

  @media only screen and (max-width: 576px) {
    width: 100%;
    max-height: 100dvh;
    height: 100%;
    border-radius: 0;
    animation: ${cardSlideUp} 0.24s cubic-bezier(0.16, 1, 0.3, 1);
  }
`;

export const DropOverlay = styled.div`
  position: absolute;
  inset: 8px;
  z-index: 5;
  pointer-events: none;
  border: 2px dashed #00a651;
  border-radius: 16px;
  background: rgba(0, 166, 81, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #0b7a45;
`;

export const ConversationModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid #eef1ef;
  flex-shrink: 0;

  h5 {
    margin: 0;
    font-weight: 600;
    color: #1f2b23;
  }

  button {
    border: none;
    background: transparent;
    color: #8b968f;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    padding: 4px;

    &:hover {
      color: #1f2b23;
    }
  }
`;

export const ConversationModalBody = styled.div`
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px 20px;
  background: #f7f9f8;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const EmptyConversationState = styled.div`
  margin: auto 0;
  text-align: center;
  color: #8b968f;
  font-size: 14px;
  padding: 0 12px;
`;

export const LoadingThreadState = styled.div`
  margin: auto 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  color: #8b968f;
  font-size: 14px;

  .spinner-border {
    color: #00a651;
    width: 2rem;
    height: 2rem;
  }
`;

export const MessageBubbleRow = styled.div`
  display: flex;
  justify-content: ${(props) => (props.$isOwn ? 'flex-end' : 'flex-start')};
`;

export const MessageBubble = styled.div`
  max-width: 78%;
  min-width: 55%;
  background: ${(props) => (props.$isOwn ? 'rgba(0, 166, 81, 0.4)' : '#ffffff')};
  color: #1f2b23;
  border: 1px solid ${(props) => (props.$isOwn ? 'rgba(0, 166, 81, 0.55)' : '#e7ebe8')};
  border-radius: 14px;
  ${(props) => (props.$isOwn ? 'border-bottom-right-radius: 4px;' : 'border-bottom-left-radius: 4px;')}
  padding: 8px 12px;
  font-size: 14px;
  word-break: break-word;

  .meta {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 4px;
  }

  .sender {
    font-size: 11px;
    font-weight: 600;
    color: ${(props) => (props.$isOwn ? '#1c4a38' : '#55635c')};
  }

  .timestamp {
    font-size: 10px;
    color: ${(props) => (props.$isOwn ? '#356152' : '#97a09b')};
    white-space: nowrap;
    flex-shrink: 0;
  }

  .body {
    white-space: pre-wrap;
  }
`;

export const MessageAttachmentChip = styled.div`
  display: inline-flex;
  cursor: pointer;
  align-items: center;
  gap: 8px;
  max-width: 100%;
  margin-top: 6px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #1f2b23;
  background: #f4f7f5;
  border: 1px solid #c7d3cc;
  border-radius: 10px;

  svg {
    color: #00a651;
    flex-shrink: 0;
  }

  span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const ConversationModalFooter = styled.div`
  flex-shrink: 0;
  border-top: 1px solid #eef1ef;
  padding: 12px 16px;
  background: #ffffff;
`;

export const PendingAttachmentsList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 8px;
`;

export const PendingAttachmentChip = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  background: #f2f4f3;
  border-radius: 8px;
  padding: 4px 6px 4px 10px;
  font-size: 12px;
  color: #1f2b23;
  max-width: 100%;

  span {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 160px;
  }

  button {
    border: none;
    background: transparent;
    color: #8b968f;
    cursor: pointer;
    display: flex;
    align-items: center;
    padding: 2px;

    &:hover {
      color: #d64545;
    }
  }
`;

export const ComposerRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
`;

export const ComposerHint = styled.p`
  margin: 8px 2px 0;
  font-size: 11px;
  line-height: 1.4;
  color: #97a09b;
  display: flex;
  align-items: baseline;
  gap: 6px;

  svg {
    flex-shrink: 0;
  }
`;

export const AttachButton = styled.label`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: 1px solid #e7ebe8;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #556058;
  cursor: pointer;
  margin: 0;

  &:hover {
    background: #f2f4f3;
  }
`;

export const ComposerTextarea = styled.textarea`
  flex: 1 1 auto;
  resize: none;
  border: 1px solid #e7ebe8;
  border-radius: 18px;
  padding: 9px 14px;
  font-size: 14px;
  max-height: 100px;
  min-height: 38px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #00a651;
    box-shadow: 0 0 0 3px rgba(0, 166, 81, 0.15);
  }
`;

export const SendButton = styled.button`
  flex-shrink: 0;
  width: 38px;
  height: 38px;
  border-radius: 50%;
  border: none;
  background: #00a651;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;

  &:disabled {
    background: #b7ddc7;
    cursor: not-allowed;
  }
`;
